/** Precise name searches and broad catalog discovery; all prices are live storefront cents. */
export function normalize(text) {
  return String(text).normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
}

export function parseBudget(text) {
  if (/\b(?:USD|EUR|GBP|AED|MYR)\b|US\$|€|£/i.test(text)) return null;
  const currency = '(?:SGD\\s*|S\\$\\s*|\\$\\s*)?';
  const amount = '(\\d+(?:\\.\\d{1,2})?)';
  const between = new RegExp('\\bbetween\\s+' + currency + amount + '\\s+(?:and|to|-)\\s+' + currency + amount + '(?:\\s*SGD)?\\b', 'i');
  const bound = new RegExp('\\b(under|below|less than|up to|at most|maximum(?: of)?|max|over|above|more than|at least)\\s+' + currency + amount + '(?:\\s*SGD)?\\b', 'i');
  let match = text.match(between), min, max;
  if (match) {
    min = Math.round(Number(match[1]) * 100); max = Math.round(Number(match[2]) * 100);
    if (min > max) return null;
  } else {
    match = text.match(bound); if (!match) return null;
    const cents = Math.round(Number(match[2]) * 100), kind = match[1].toLowerCase();
    if (['under', 'below', 'less than'].includes(kind)) max = cents - 1;
    else if (['over', 'above', 'more than'].includes(kind)) min = cents + 1;
    else if (kind === 'at least') min = cents;
    else max = cents;
  }
  return { query: text.replace(match[0], ' ').replace(/\s+/g, ' ').trim(), min, max };
}

const aliases = new Map([['latafa', 'lattafa'], ['riffs', 'riiffs'], ['alharamain', 'al haramain'], ['swissarabian', 'swiss arabian'], ['arabiyat', 'arabiyat prestige']]);
const scentWords = new Set(['vanilla', 'oud', 'musk', 'rose', 'amber', 'sandalwood', 'jasmine', 'citrus', 'woody', 'floral', 'sweet', 'fresh', 'fruity', 'leather', 'tobacco', 'gourmand']);
const filler = new Set(['show', 'me', 'find', 'please', 'a', 'an', 'the', 'with', 'and', 'for', 'perfume', 'perfumes', 'fragrance', 'fragrances', 'scent', 'scents']);
function stem(text) {
  return normalize(text).replace(/\b\d+(?:\s+\d+)?\s*(?:ml|millilitres?|milliliters?)\b/g, '')
    .replace(/\b(?:edp|edt|extrait|eau de parfum|eau de toilette|de parfum)\b/g, '')
    .replace(/\s+/g, ' ').trim();
}
function tokenMatch(token, haystack) {
  return haystack.some(word => word === token || (token.length >= 3 && word.startsWith(token)));
}

export function resolveSearch(text, catalog) {
  const budget = catalog.currency === 'SGD' ? parseBudget(text) : null;
  const input = normalize(budget?.query ?? text);
  const tokens = input.split(' ').filter(t => t && !filler.has(t));
  const cleaned = tokens.join(' ');
  const brands = [...new Set(catalog.products.map(p => p.vendor))];
  const wantedBrand = aliases.get(cleaned) ?? cleaned;
  let matches, intent = 'discovery';
  const brand = brands.find(b => normalize(b) === wantedBrand);
  if (brand) {
    matches = catalog.products.filter(p => p.vendor === brand); intent = 'brand';
  } else {
    const exact = catalog.products.filter(p => normalize(p.title) === input || stem(p.title) === cleaned);
    if (exact.length) { matches = exact; intent = 'exact'; }
    else {
      // A bare family name can be ambiguous: keep original and flankers rather than selecting one.
      const family = scentWords.has(cleaned) ? [] : catalog.products.filter(p => {
        const full = stem(p.title), vendor = normalize(p.vendor);
        const short = full.startsWith(vendor + ' ') ? full.slice(vendor.length + 1) : full;
        return cleaned && (short === cleaned || short.startsWith(cleaned + ' ') || full.startsWith(cleaned + ' '));
      });
      if (family.length) { matches = family; intent = 'family'; }
      else {
        matches = catalog.products.filter(p => {
          const words = normalize([p.title, p.vendor, p.description, ...(p.tags ?? [])].join(' ')).split(' ');
          return tokens.every(t => tokenMatch(aliases.get(t) ?? t, words));
        });
      }
    }
  }
  if (budget) matches = matches.filter(p => p.prices.some(c => (budget.min === undefined || c >= budget.min) && (budget.max === undefined || c <= budget.max)));
  // The sentinel keeps an empty set empty and identifies this flow separately from recently viewed.
  const query = matches.map(p => 'id:' + p.id).join(' OR ') + (matches.length ? ' OR id:0' : 'id:0 OR id:0');
  return { intent, ids: matches.map(p => p.id), query, budget };
}

let catalogPromise;
export function loadCatalog() {
  if (catalogPromise) return catalogPromise;
  catalogPromise = (async () => {
    let url = new URL((window.Shopify?.routes?.root ?? '/') + '?section_id=gf-search-index', location.origin);
    const products = [], seen = new Set();
    let currency;
    while (url) {
      if (seen.has(url.href) || seen.size >= 100) throw Error('Search catalog pagination did not complete');
      seen.add(url.href);
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) throw Error('Search catalog unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = html.querySelector('[data-gf-search-index]');
      if (!node) throw Error('Search catalog missing');
      const data = JSON.parse(node.textContent);
      products.push(...data.products); currency = data.currency;
      url = data.next ? new URL(data.next, location.origin) : null;
      if (url) url.searchParams.set('section_id', 'gf-search-index');
    }
    return { products: [...new Map(products.map(p => [p.id, p])).values()], currency };
  })().catch(error => { catalogPromise = undefined; throw error; });
  return catalogPromise;
}

export function searchUrl(action, text, catalog) {
  const resolved = resolveSearch(text, catalog);
  const url = new URL(action, typeof location === 'undefined' ? 'https://gulffragrances.com.sg' : location.origin);
  // Preserve explicit preview parameters when using a share link.
  if (typeof location !== 'undefined') {
    const current = new URL(location.href);
    for (const key of ['preview_theme_id', 'pb', '_fd']) if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
  }
  url.searchParams.set('q', resolved.query);
  url.searchParams.set('gf_q', text);
  url.searchParams.set('type', 'product');
  url.searchParams.set('options[prefix]', 'none');
  return url;
}

export function restoreSearchCopy(root, original, query) {
  for (const element of root.querySelectorAll('[data-gf-search-input]')) element.value = original;
  for (const element of root.querySelectorAll('[data-gf-search-copy]')) {
    for (const node of element.childNodes) if (node.nodeType === 3) node.textContent = node.textContent.replace(query, original);
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[role="search"]')) return;
    const input = form.querySelector('input[name="q"]');
    if (!input || !input.value.trim()) return;
    event.preventDefault();
    const text = input.value;
    try { location.assign(searchUrl(form.action, text, await loadCatalog()).toString()); }
    catch (error) {
      const component = form.closest('predictive-search-component');
      const status = document.createElement('p');
      status.setAttribute('role', 'status');
      status.dataset.gfSearchErrorMessage = '';
      status.textContent = component?.dataset.gfSearchError ?? form.dataset.gfSearchError;
      const container = component?.querySelector('[ref="predictiveSearchResults"]');
      if (container) container.replaceChildren(status);
      else { form.querySelector('[data-gf-search-error-message]')?.remove(); form.append(status); }
      console.error('Catalog search unavailable', error);
    }
  }, true);
  const current = new URL(location.href);
  const original = current.searchParams.get('gf_q');
  if (original !== null) {
    restoreSearchCopy(document, original, current.searchParams.get('q') ?? '');
    document.title = document.title.replace(current.searchParams.get('q') ?? '', original);
  }
}
